import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { formatCurrency } from "@/lib/utils";
import {
  Building2,
  Users,
  ArrowRight,
  CheckCircle2,
  Clock,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Package,
  Coins,
  Star,
  FileText,
  CalendarDays,
  BarChart3,
  Zap,
  MapPin,
  UserMinus,
} from "lucide-react";

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  loading,
}: {
  label: string;
  value?: number | string;
  sub?: string;
  icon: React.ElementType;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value ?? "—"}</div>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Brouillon",
  PUBLISHED: "Publié",
  IN_PROGRESS: "En cours",
  COMPLETED: "Terminé",
  CANCELLED: "Annulé",
};

const PERIOD_LABELS: Record<string, string> = {
  "7d": "7 jours",
  "30d": "30 jours",
  "90d": "90 jours",
};

const METRIC_CONFIG: Record<string, { label: string; icon: React.ElementType; format?: "percent" | "currency" }> = {
  newProjects: { label: "Nouveaux projets", icon: Package },
  newArtisans: { label: "Nouveaux artisans", icon: Users },
  creditsPurchased: { label: "Crédits achetés", icon: Coins },
  leadsUnlocked: { label: "Leads débloqués", icon: ArrowRight },
  quotesSent: { label: "Devis envoyés", icon: FileText },
  quotesAccepted: { label: "Devis acceptés", icon: CheckCircle2 },
  conversionRate: { label: "Taux de conversion", icon: TrendingUp, format: "percent" },
  newReviews: { label: "Nouveaux avis", icon: Star },
};

function MetricCard({
  label,
  value,
  change,
  icon: Icon,
  format,
  loading,
}: {
  label: string;
  value?: number;
  change?: number;
  icon: React.ElementType;
  format?: "percent" | "currency";
  loading?: boolean;
}) {
  const displayValue = value != null
    ? format === "percent" ? `${value}%` : value.toLocaleString("fr-FR")
    : "—";
  const isPositive = (change ?? 0) > 0;
  const isNeutral = change === 0 || change == null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <>
            <div className="text-2xl font-bold">{displayValue}</div>
            {!isNeutral && (
              <div className={`flex items-center gap-1 text-xs mt-1 ${isPositive ? "text-success" : "text-destructive"}`}>
                {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {isPositive ? "+" : ""}{change}% vs période précédente
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const PERIOD_DAYS: Record<string, number> = { "7d": 7, "30d": 30, "90d": 90 };

function ChangeIndicator({ current, previous, suffix = "" }: { current?: number; previous?: number; suffix?: string }) {
  if (current == null || previous == null || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return null;
  const isPositive = pct > 0;
  return (
    <div className={`flex items-center gap-1 text-xs mt-1 ${isPositive ? "text-green-600" : "text-destructive"}`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isPositive ? "+" : ""}{pct}%{suffix}
    </div>
  );
}

export default function TravauxBTPSupervision() {
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");

  const { data: overview, isLoading: ovLoading, refetch } = trpc.admin.travauxbtp.overview.useQuery();
  const { data: metrics, isLoading: metricsLoading } = trpc.admin.travauxbtp.getMetrics.useQuery({ period } as any);
  const { data: funnel, isLoading: funLoading } = trpc.admin.travauxbtp.conversionFunnel.useQuery();
  const { data: cross, isLoading: crossLoading } = trpc.admin.travauxbtp.crossConversion.useQuery();
  const { data: dashboard, isLoading: dashLoading } = trpc.admin.travauxbtp.getDashboard.useQuery();
  const { data: churnStats, isLoading: churnLoading } = trpc.admin.travauxbtp.getChurnStats.useQuery({ months: 6 });
  const { data: topVilles, isLoading: villesLoading } = trpc.admin.travauxbtp.getTopVilles.useQuery({ period, limit: 10 });
  const {
    data: pending,
    isLoading: pendingLoading,
    refetch: refetchPending,
  } = trpc.admin.travauxbtp.listPendingArtisans.useQuery({ page: 1, limit: 20 });

  const approveArtisan = trpc.admin.travauxbtp.approveArtisan.useMutation({
    onSuccess: () => refetchPending(),
  });
  const suspendArtisan = trpc.admin.travauxbtp.suspendArtisan.useMutation({
    onSuccess: () => refetchPending(),
  });

  const maxFunnel = funnel?.steps?.[0]?.count ?? 1;

  const leadsPerDay = (metrics as any)?.metrics?.leadsUnlocked?.value != null
    ? Math.round((metrics as any).metrics.leadsUnlocked.value / PERIOD_DAYS[period])
    : null;

  const creditEvoMax = dashboard?.creditEvolution
    ? Math.max(...(dashboard.creditEvolution as any[]).map((e: any) => e.total), 1)
    : 1;

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">TravauxBTP — Supervision</h1>
            <p className="text-muted-foreground">KPIs business, marketplace, funnel de conversion et modération</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* ═══ BUSINESS KPIs ═══ */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            KPIs Business
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">CA Crédits (mois)</CardTitle>
                <Coins className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                {dashLoading ? <Skeleton className="h-8 w-24" /> : (
                  <>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency((dashboard as any)?.creditsSoldMonth ?? 0)}
                    </div>
                    <ChangeIndicator
                      current={(dashboard as any)?.creditsSoldMonth}
                      previous={(dashboard as any)?.creditsSoldPrevMonth}
                      suffix=" vs mois préc."
                    />
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Leads / jour</CardTitle>
                <Zap className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                {metricsLoading ? <Skeleton className="h-8 w-16" /> : (
                  <>
                    <div className="text-2xl font-bold">{leadsPerDay ?? "—"}</div>
                    <p className="text-xs text-muted-foreground">
                      {(metrics as any)?.metrics?.leadsUnlocked?.value ?? 0} sur {PERIOD_DAYS[period]}j
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taux conversion devis</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                {dashLoading ? <Skeleton className="h-8 w-16" /> : (
                  <>
                    <div className="text-2xl font-bold">{(dashboard as any)?.acceptanceRate ?? 0}%</div>
                    <p className="text-xs text-muted-foreground">
                      {(dashboard as any)?.acceptedQuotes ?? 0} / {(dashboard as any)?.totalQuotes ?? 0} devis
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Artisans actifs (30j)</CardTitle>
                <Users className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                {dashLoading ? <Skeleton className="h-8 w-16" /> : (
                  <>
                    <div className="text-2xl font-bold">{(dashboard as any)?.activeArtisans ?? 0}</div>
                    <p className="text-xs text-muted-foreground">
                      Rev. moy. {formatCurrency((dashboard as any)?.avgRevenuePerArtisan ?? 0)}/artisan
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ═══ Credit Evolution + Top Artisans ═══ */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Coins className="h-4 w-4" />
                Évolution crédits (6 mois)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : !(dashboard as any)?.creditEvolution?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">Aucune donnée</p>
              ) : (
                <div className="space-y-2">
                  {((dashboard as any).creditEvolution as any[]).map((entry: any) => {
                    const pct = creditEvoMax > 0 ? Math.round((entry.total / creditEvoMax) * 100) : 0;
                    return (
                      <div key={entry.month} className="flex items-center gap-3">
                        <div className="w-16 text-xs text-muted-foreground font-mono">{entry.month}</div>
                        <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="w-16 text-xs font-medium text-right">{entry.total}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-4 w-4" />
                Top artisans (leads débloqués)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {dashLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : !(dashboard as any)?.topArtisans?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">Aucun artisan actif</p>
              ) : (
                <div className="divide-y">
                  {((dashboard as any).topArtisans as any[]).map((a: any, idx: number) => (
                    <div key={a.companyId} className="flex items-center justify-between px-4 py-3 text-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-muted-foreground w-5">{idx + 1}.</span>
                        <span className="font-medium">{a.companyName}</span>
                      </div>
                      <Badge variant="secondary">{a.unlocks} leads</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ═══ OVERVIEW KPIs ═══ */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Projets total"
            value={overview?.projects.total}
            sub={`${overview?.projects.byStatus?.["PUBLISHED"] ?? 0} publiés`}
            icon={Package}
            loading={ovLoading}
          />
          <KpiCard
            label="Artisans"
            value={overview?.artisans.total}
            sub={`${overview?.artisans.verified ?? 0} vérifiés · ${overview?.artisans.pending ?? 0} en attente`}
            icon={Users}
            loading={ovLoading}
          />
          <KpiCard
            label="Matchings"
            value={overview?.matches.total}
            icon={ArrowRight}
            loading={ovLoading}
          />
          <KpiCard
            label="Devis acceptés"
            value={overview?.quotes.accepted}
            sub={`sur ${overview?.quotes.total ?? 0} devis`}
            icon={CheckCircle2}
            loading={ovLoading}
          />
        </div>

        {/* Metrics with period selector */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Métriques par période
            </h2>
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              {(["7d", "30d", "90d"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    period === p
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {Object.entries(METRIC_CONFIG).map(([key, cfg]) => {
              const m = (metrics as any)?.metrics?.[key];
              return (
                <MetricCard
                  key={key}
                  label={cfg.label}
                  value={cfg.format === "percent" ? m : m?.value}
                  change={cfg.format === "percent" ? undefined : m?.change}
                  icon={cfg.icon}
                  format={cfg.format}
                  loading={metricsLoading}
                />
              );
            })}
          </div>
        </div>

        {/* Catégories */}
        {overview?.categories && overview.categories.length > 0 && (
          <div>
            <h2 className="text-base font-semibold mb-3">Répartition par catégorie</h2>
            <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4">
              {overview.categories.slice(0, 8).map((c: any) => (
                <div key={c.category} className="flex items-center justify-between border rounded-lg p-3 text-sm">
                  <span className="text-muted-foreground truncate">{c.category || "—"}</span>
                  <Badge variant="secondary">{c.count}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Funnel */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Funnel de conversion
          </h2>
          <Card>
            <CardContent className="p-6 space-y-4">
              {funLoading
                ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)
                : funnel?.steps.map((step, idx) => {
                    const pct = maxFunnel > 0 ? Math.round((step.count / maxFunnel) * 100) : 0;
                    const LABELS: Record<string, string> = {
                      published: "Projets publiés",
                      matched: "Avec matching",
                      quoted: "Avec devis",
                      in_progress: "En cours",
                      completed: "Terminés",
                    };
                    return (
                      <div key={step.label} className="flex items-center gap-4">
                        <div className="w-36 text-sm text-muted-foreground">{LABELS[step.label] ?? step.label}</div>
                        <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="w-24 text-sm font-medium text-right">
                          {step.count.toLocaleString("fr-FR")}
                          <span className="text-muted-foreground ml-1 text-xs">({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
            </CardContent>
          </Card>
        </div>

        {/* Cross-conversion */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Conversion TravauxBTP → SaaS
          </h2>
          <div className="grid gap-4 md:grid-cols-3 mb-4">
            {crossLoading ? (
              Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)
            ) : (
              <>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">{cross?.totalTravauxbtpCompanies ?? 0}</div>
                    <p className="text-xs text-muted-foreground">Entreprises sur TravauxBTP</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">{cross?.totalAlsoUsingSaas ?? 0}</div>
                    <p className="text-xs text-muted-foreground">Utilisent aussi le SaaS</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-green-600">{cross?.conversionRate ?? 0}%</div>
                    <p className="text-xs text-muted-foreground">Taux de conversion</p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          {cross && cross.companies.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {cross.companies.map((c: any) => (
                    <div key={c.id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <div className="font-medium">{c.name}</div>
                      <div className="flex items-center gap-4 text-muted-foreground text-xs">
                        <span>{c.matchCount} matchings</span>
                        <span>{c.quoteCount} devis</span>
                        <Badge variant="secondary">{c.plan ?? "—"}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ═══ CHURN ARTISANS ═══ */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <UserMinus className="h-4 w-4 text-destructive" />
            Churn artisans (6 mois)
          </h2>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Taux de churn mensuel</CardTitle>
              </CardHeader>
              <CardContent>
                {churnLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                  </div>
                ) : !churnStats?.monthlyStats?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Aucune donnée</p>
                ) : (
                  <div className="space-y-2">
                    {churnStats.monthlyStats.map((s) => (
                      <div key={s.month} className="flex items-center gap-3">
                        <div className="w-16 text-xs text-muted-foreground font-mono">{s.month}</div>
                        <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                          <div
                            className="h-full bg-destructive/70 rounded-full transition-all"
                            style={{ width: `${Math.min(s.churnRate * 5, 100)}%` }}
                          />
                        </div>
                        <div className="w-20 text-xs font-medium text-right">
                          {s.churnRate}%
                          <span className="text-muted-foreground ml-1">({s.churned})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Artisans récemment perdus</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {churnLoading ? (
                  <div className="p-6 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : !churnStats?.recentArtisans?.length ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    Aucun départ récent
                  </div>
                ) : (
                  <div className="divide-y max-h-80 overflow-y-auto">
                    {churnStats.recentArtisans.map((a) => (
                      <div key={a.id} className="flex items-center justify-between px-4 py-3 text-sm">
                        <div>
                          <div className="font-medium">{a.companyName || a.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(a.date).toLocaleDateString("fr-FR")}
                          </div>
                        </div>
                        <Badge variant={a.reason === "Compte supprimé" ? "destructive" : "secondary"}>
                          {a.reason}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ═══ TOP VILLES ═══ */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            Top villes ({PERIOD_LABELS[period]})
          </h2>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Par projets déposés</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {villesLoading ? (
                  <div className="p-6 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                  </div>
                ) : !topVilles?.topVillesByProjects?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Aucune donnée</p>
                ) : (
                  <div className="divide-y">
                    {topVilles.topVillesByProjects.map((v, idx) => (
                      <div key={v.city} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-muted-foreground w-5">{idx + 1}.</span>
                          <span className="font-medium">{v.city}</span>
                        </div>
                        <Badge variant="secondary">{v.count} projets</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Par artisans inscrits</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {villesLoading ? (
                  <div className="p-6 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                  </div>
                ) : !topVilles?.topVillesByArtisans?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Aucune donnée</p>
                ) : (
                  <div className="divide-y">
                    {topVilles.topVillesByArtisans.map((v, idx) => (
                      <div key={v.city} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-muted-foreground w-5">{idx + 1}.</span>
                          <span className="font-medium">{v.city ?? "—"}</span>
                        </div>
                        <Badge variant="secondary">{v.count} artisans</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Pending artisan moderation */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Artisans en attente de vérification
            {(pending?.total ?? 0) > 0 && (
              <Badge variant="destructive">{pending?.total}</Badge>
            )}
          </h2>
          <Card>
            <CardContent className="p-0">
              {pendingLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : !pending?.items.length ? (
                <div className="p-6 text-center text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  Aucun artisan en attente — file vide
                </div>
              ) : (
                <div className="divide-y">
                  {pending.items.map((a: any) => (
                    <div key={a.id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <div>
                        <div className="font-medium">{a.companyName ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {a.User?.email} · inscrit le {new Date(a.createdAt).toLocaleDateString("fr-FR")}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => approveArtisan.mutate({ artisanProfileId: a.id })}
                          disabled={approveArtisan.isPending}
                        >
                          Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (confirm("Suspendre cet artisan ?")) {
                              suspendArtisan.mutate({ artisanProfileId: a.id });
                            }
                          }}
                          disabled={suspendArtisan.isPending}
                        >
                          Suspendre
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
